import React, { useState, useMemo, useCallback } from 'react';
import { t } from 'ttag';
import PropTypes from 'prop-types';
import { Price } from 'components';

function ChoiceSelector({
  customizationOptions,
  updateCustomizationOptions,
  setFieldValue,
}) {
  const [value, setValue] = useState(undefined);

  const customizationChoices = useMemo(() => ['custom', 'player_flag'], []);

  const divClassNames = useCallback(
    (customizationChoice) =>
      [
        'flex',
        'h-12',
        'w-full',
        'items-center',
        'justify-center',
        'border',
        'border-solid',
        customizationChoice === value ? 'border-black' : 'border-gray',
        customizationChoice === value ? 'bg-black' : 'bg-white',
        customizationChoice === value ? 'text-white' : 'text-black',
        'text-sm',
        'last:border-l-0',
        'hover:cursor-pointer',
      ].join(' '),
    [value]
  );

  const getPrice = useCallback(() => {
    if (
      !value ||
      !customizationOptions ||
      !customizationOptions.name ||
      !customizationOptions.player_flag
    )
      return undefined;
    if (value === 'custom')
      return parseInt(customizationOptions.name.price, 10);
    if (value === 'player_flag')
      return parseInt(customizationOptions.player_flag.price, 10);
    return undefined;
  }, [customizationOptions, value]);

  const handleClick = useCallback((customizationChoice) => {
    setValue(customizationChoice);
    if (customizationChoice === 'custom') {
      // Reset the Field Value for the Player Flag
      setFieldValue('player_flag', 0);
      // Show the Name and Number Fields.
      updateCustomizationOptions((draft) => ({
        ...draft,
        name: { ...draft.name, isVisible: true },
        number: { ...draft.number, isVisible: true },
        player_flag: { ...draft.player_flag, isVisible: false },
      }));
    }
    if (customizationChoice === 'player_flag') {
      // Reset the Field Value for the Name and Number.
      setFieldValue('name', undefined);
      setFieldValue('number', undefined);
      // Show the Player Flag Field.
      updateCustomizationOptions((draft) => ({
        ...draft,
        name: { ...draft.name, isVisible: false },
        number: { ...draft.number, isVisible: false },
        player_flag: { ...draft.player_flag, isVisible: true },
      }));
    }
  }, []);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-bold uppercase">{t`Print`}</span>
        {getPrice() && (
          <Price
            unitPrice={getPrice()}
            priceSize="xs"
            priceFontWeight="semibold"
          />
        )}
      </div>
      <div className="flex" role="group">
        {customizationChoices.map((customizationChoice) => (
          <div
            role="button"
            key={customizationChoice}
            className={divClassNames(customizationChoice)}
            tabIndex={0}
            onClick={() => handleClick(customizationChoice)}
          >
            <span>
              {customizationChoice === 'custom' ? t`Your own` : t`Player name`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

ChoiceSelector.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  customizationOptions: PropTypes.object.isRequired,
  updateCustomizationOptions: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

ChoiceSelector.defaultProps = {};

export default ChoiceSelector;
